import {
    HighlightedObject,
    isAreaEmissionInfo,
    isPlumeEmissionInfo,
    MethaneLayerIds,
    MethaneLayerState,
    MSatPickInfo,
    NothingHighlighted,
    OGILayerIds,
    OGILayerState,
    PickInfoToPrioritize
} from '../types';
import { getFeatureIdFromPickInfo, getRootId } from './featureHelpers';

/** Helper function to update the highlights of the Methane and OGI Layers */
export const updateLayerHighlight = <T extends MethaneLayerState | OGILayerState>(params: {
    state: T;
    /**
     * This string uses MapStateLayerNames as a root, but
     * appends the item or parent item id to uniquely identify it
     */
    layerId: string;
    objectId: string | null;
    object: HighlightedObject;
    coordinate?: number[];
}) => {
    const { state, layerId, objectId, object, coordinate } = params;
    const layerKeys = Object.keys(state) as Array<keyof T>;

    layerKeys.forEach((key) => {
        const isLayer = key === getRootId(layerId);
        const updatedObject = { ...object, ...(coordinate && { coordinate }), layerId };

        const update = isLayer
            ? {
                  highlightedFeatureId: objectId,
                  highlightedObject: updatedObject
              }
            : {
                  highlightedFeatureId: NothingHighlighted.highlightedFeatureId,
                  highlightedObject: null
              };

        state[key] = {
            ...state[key],
            ...update
        };
    });
};

/** Helper function to clear the highlights of the Methane and OGI Layers */
export const removeLayerHighlights = <T extends MethaneLayerState | OGILayerState>(state: T) => {
    const layerKeys = Object.keys(state) as Array<keyof T>;
    layerKeys.forEach((key) => {
        const highlightByFeatureId =
            key === MethaneLayerIds.plumeEmissionRate ||
            key === MethaneLayerIds.targets ||
            OGILayerIds.tileInfrastructure;

        const update = {
            highlightedObject: null,
            ...(highlightByFeatureId && { highlightedFeatureId: NothingHighlighted.highlightedFeatureId })
        };

        state[key] = {
            ...state[key],
            ...update
        };
    });
};

/** Creates the payload for `addOneMapFeatureHighlight` and `updateLayerHighlight` */
export const createHighlightPayload = (info: PickInfoToPrioritize | MSatPickInfo) => {
    const layerId = info.layer.id;
    let objectId;
    let object;
    let coordinate;
    if ('bitmap' in info && info.bitmap) {
        objectId = info.bitmap.pixel.join('-');
        object = info.bitmap;
        coordinate = info.coordinate;
    } else {
        object = info.object;
        objectId = getFeatureIdFromPickInfo(info);
        if (isPlumeEmissionInfo(info)) {
            coordinate = info.object.geometry.coordinates;
        } else if (isAreaEmissionInfo(info)) {
            coordinate = info.coordinate;
        }
    }
    return { layerId, objectId, object, coordinate };
};
